import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, RADIUS } from "../theme";
import Card from "./ui/Card";
import Btn from "./ui/Btn";
import Input from "./ui/Input";
import Badge from "./ui/Badge";
import EstadoCarregando from "./ui/EstadoCarregando";

// Autoconível: usado tanto em Meus Dados (gestor/funcionário) quanto numa
// tela equivalente do admin — só depende de estar autenticado, nada de
// perfil específico.
export default function DoisFatores() {
  const { token } = useAuth();
  const [ativo, setAtivo] = useState(null);
  const [configurando, setConfigurando] = useState(false);
  const [uri, setUri] = useState(null);
  const [secret, setSecret] = useState(null);
  const [codigo, setCodigo] = useState("");
  const [senhaDesativar, setSenhaDesativar] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [processando, setProcessando] = useState(false);

  const carregarStatus = useCallback(async () => {
    try {
      const data = await apiFetch("GET", "/api/auth/2fa/status", undefined, token);
      setAtivo(data.ativo);
    } catch {
      setAtivo(false);
    }
  }, [token]);

  useEffect(() => { carregarStatus(); }, [carregarStatus]);

  const iniciar = async () => {
    setErro("");
    setSucesso("");
    setProcessando(true);
    try {
      const data = await apiFetch("POST", "/api/auth/2fa/iniciar", undefined, token);
      setUri(data.uri);
      setSecret(data.secret);
      setCodigo("");
      setConfigurando(true);
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const confirmar = async () => {
    setErro("");
    setProcessando(true);
    try {
      await apiFetch("POST", "/api/auth/2fa/confirmar", { codigo }, token);
      setConfigurando(false);
      setUri(null);
      setSecret(null);
      setCodigo("");
      setSucesso("Autenticação em duas etapas ativada.");
      await carregarStatus();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  const desativar = async () => {
    setErro("");
    setSucesso("");
    setProcessando(true);
    try {
      await apiFetch("POST", "/api/auth/2fa/desativar", { senha: senhaDesativar }, token);
      setSenhaDesativar("");
      setSucesso("Autenticação em duas etapas desativada.");
      await carregarStatus();
    } catch (e) {
      setErro(e.message);
    } finally {
      setProcessando(false);
    }
  };

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, color: COLORS.text, margin: 0 }}>Autenticação em duas etapas</h2>
        {ativo === true && <Badge tone="success">Ativa</Badge>}
      </div>
      <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 16px" }}>
        Além da senha, pede um código do celular (Google Authenticator, Microsoft Authenticator, Authy) a cada login.
      </p>

      {ativo === null ? (
        <EstadoCarregando padding={0} />
      ) : ativo === false && !configurando ? (
        <Btn variant="secondary" onClick={iniciar} disabled={processando}>
          {processando ? "Gerando..." : "Ativar"}
        </Btn>
      ) : configurando ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <p style={{ fontSize: 13, color: COLORS.textMid, margin: 0 }}>
            No celular, toque no botão abaixo pra abrir direto no seu app autenticador (Google Authenticator, Authy). No computador, adicione uma conta manualmente e digite o código abaixo.
          </p>
          {uri && (
            <a
              href={uri}
              style={{
                textAlign: "center",
                fontSize: 13,
                fontWeight: 700,
                color: COLORS.primary,
                background: COLORS.bg,
                border: `1.5px solid ${COLORS.primary}`,
                borderRadius: RADIUS.md,
                padding: "10px 14px",
                textDecoration: "none",
              }}
            >
              Abrir no app autenticador
            </a>
          )}
          {secret && (
            <div style={{ fontSize: 12, color: COLORS.textMid, textAlign: "center" }}>
              Código pra digitar manualmente:
              <br />
              <code style={{ background: COLORS.bg, padding: "2px 6px", borderRadius: 4, fontWeight: 700, letterSpacing: "0.05em" }}>{secret}</code>
            </div>
          )}
          <Input
            label="Código de 6 dígitos"
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="000000"
            maxLength={6}
            inputMode="numeric"
          />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: RADIUS.sm, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="secondary" onClick={() => { setConfigurando(false); setErro(""); }} style={{ flex: 1 }}>
              Cancelar
            </Btn>
            <Btn onClick={confirmar} disabled={processando || codigo.trim().length < 6} style={{ flex: 1 }}>
              {processando ? "Confirmando..." : "Confirmar"}
            </Btn>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Input
            label="Confirme sua senha pra desativar"
            type="password"
            value={senhaDesativar}
            onChange={(e) => setSenhaDesativar(e.target.value)}
            placeholder="••••••••"
          />
          {erro && (
            <div style={{ color: COLORS.danger, fontSize: 13, background: COLORS.dangerBg, border: "1px solid #FECACA", borderRadius: RADIUS.sm, padding: "9px 13px" }}>
              {erro}
            </div>
          )}
          <Btn variant="secondary" onClick={desativar} disabled={processando || !senhaDesativar} style={{ alignSelf: "flex-start", borderColor: COLORS.danger, color: COLORS.danger }}>
            {processando ? "Desativando..." : "Desativar"}
          </Btn>
        </div>
      )}

      {sucesso && (
        <div style={{ color: COLORS.success, fontSize: 13, background: COLORS.successBg, border: "1px solid #BBF7D0", borderRadius: RADIUS.sm, padding: "9px 13px", marginTop: 14 }}>
          {sucesso}
        </div>
      )}
    </Card>
  );
}
